"use client";

import { authedFetch, isSignedIn } from "@/lib/api-client";
import { appGetItem, appSetItem } from "@/lib/auth-storage";

export interface ActivityLogEntry {
  id: string;
  timestamp: string; // ISO date string
  action: string; // machine-readable action code
  detail: string; // human-readable description
  data?: Record<string, unknown>;
  source?: string;
  synced?: boolean; // client-side flag: saved to database or not
}

const STORAGE_KEY = "jupas_activity_logs";
const MAX_LOCAL_ENTRIES = 300;
const EVENT_NAME = "jupas-activity-log-updated";
const FLUSH_DELAY_MS = 1500;

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;

function generateId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readLogs(): ActivityLogEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = appGetItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLogs(logs: ActivityLogEntry[]): void {
  if (typeof window === "undefined") return;

  appSetItem(STORAGE_KEY, JSON.stringify(logs.slice(0, MAX_LOCAL_ENTRIES)));
  window.dispatchEvent(new CustomEvent(EVENT_NAME));
}

/**
 * Record a user action. Stored locally immediately and flushed to the
 * database (debounced) when the user is signed in.
 */
export function logActivity(
  action: string,
  detail: string,
  data?: Record<string, unknown>
): void {
  const entry: ActivityLogEntry = {
    id: generateId(),
    timestamp: new Date().toISOString(),
    action,
    detail,
    data,
    source: "web",
    synced: false,
  };
  writeLogs([entry, ...readLogs()].slice(0, MAX_LOCAL_ENTRIES));
  scheduleFlush();
}

function scheduleFlush(): void {
  if (typeof window === "undefined") return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    void flushActivityLogs();
  }, FLUSH_DELAY_MS);
}

/**
 * Send all unsynced local entries to the database.
 * Returns false when not signed in or the request fails.
 */
export async function flushActivityLogs(): Promise<boolean> {
  if (isFlushing) return false;

  const unsynced = readLogs().filter((entry) => !entry.synced);
  if (unsynced.length === 0) return true;

  isFlushing = true;
  try {
    // Only send to the database when there is an active session;
    // otherwise keep the entries local for the next sign-in.
    if (!(await isSignedIn())) {
      return false;
    }

    const response = await authedFetch("/api/activity-log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        entries: unsynced.map(({ synced: _synced, ...rest }) => rest),
      }),
    });

    if (response.ok) {
      const data = (await response.json()) as { savedIds?: string[] };
      const savedIds = new Set(data.savedIds ?? unsynced.map((entry) => entry.id));
      writeLogs(
        readLogs().map((entry) =>
          savedIds.has(entry.id) ? { ...entry, synced: true } : entry
        )
      );
      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to flush activity logs:", error);
    return false;
  } finally {
    isFlushing = false;
  }
}

/**
 * Fetch the signed-in user's logs from the database and merge them into the
 * local store. Returns the number of new entries added.
 */
export async function loadActivityLogsFromServer(): Promise<number> {
  try {
    // Skip when signed out; the server would reject the request anyway
    if (!(await isSignedIn())) return 0;

    const response = await authedFetch("/api/activity-log");
    if (!response.ok) return 0;

    const data = (await response.json()) as { logs?: ActivityLogEntry[] };
    const serverLogs = (data.logs ?? []).map((entry) => ({
      ...entry,
      synced: true,
    }));
    if (serverLogs.length === 0) return 0;

    const byId = new Map(readLogs().map((entry) => [entry.id, entry]));
    let added = 0;
    for (const entry of serverLogs) {
      if (!byId.has(entry.id)) {
        byId.set(entry.id, entry);
        added++;
      }
    }

    const merged = [...byId.values()]
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
      .slice(0, MAX_LOCAL_ENTRIES);
    writeLogs(merged);
    return added;
  } catch (error) {
    console.error("Failed to load activity logs from server:", error);
    return 0;
  }
}

/** All local log entries, newest first. */
export function getActivityLogs(): ActivityLogEntry[] {
  return readLogs();
}

/** Remove all locally cached logs (e.g. on sign-out). */
export function clearLocalActivityLogs(): void {
  writeLogs([]);
}

/** Subscribe to local log changes; returns an unsubscribe function. */
export function subscribeActivityLogs(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
